'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/cn';

/** Radice del dialog modale: gestisce apertura/chiusura, va usata con `open`/`onOpenChange`. */
const Dialog = DialogPrimitive.Root;

/** Elemento che apre il dialog al click. */
const DialogTrigger = DialogPrimitive.Trigger;

/** Portale che renderizza il contenuto del dialog fuori dal flusso DOM del genitore. */
const DialogPortal = DialogPrimitive.Portal;

/** Elemento che chiude il dialog al click. */
const DialogClose = DialogPrimitive.Close;

/** Props di {@link DialogOverlay}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface DialogOverlayProps extends React.ComponentPropsWithoutRef<
	typeof DialogPrimitive.Overlay
> {}

/**
 * Sfondo scurito dietro il dialog.
 *
 * Colore fisso (`rgba(5,13,26,.88)`), non `bg-bg/88`: il mock scrive
 * `.modal-overlay{background:rgba(5,13,26,.88)}` come letterale, indipendente
 * dal tema — `--color-bg` invece schiarisce in tema chiaro (diventa quasi
 * bianco), e l'overlay perdeva contrasto e "sbiadiva" tutta la piattaforma
 * dietro invece di scurirla. Stesso principio dei colori fissi dei grafici
 * (`--color-series-N`), qui applicato a un overlay invece che a una serie.
 */
const DialogOverlay = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Overlay>,
	DialogOverlayProps
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		ref={ref}
		className={cn(
			'fixed inset-0 z-50 bg-[rgba(5,13,26,0.88)] backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			className,
		)}
		{...props}
	/>
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DIALOG_WIDTH = {
	sm: 'max-w-sm',
	md: 'max-w-md',
	lg: 'max-w-lg',
	xl: 'max-w-2xl',
} as const;

export interface DialogContentExtraProps {
	/** Se true, nasconde il bottone di chiusura in alto a destra. */
	hideCloseButton?: boolean;
	/** Larghezza massima del pannello. Default: `lg`. */
	width?: keyof typeof DIALOG_WIDTH;
	/** Sfondo tono card (`bg-card`) invece dello sfondo pannello di default. */
	tone?: 'panel' | 'card';
	/** Se true, limita l'altezza al viewport e rende scrollabile il contenuto. */
	scrollable?: boolean;
}

/** Pannello del dialog (contenuto vero e proprio, con overlay e animazioni). */
const DialogContent = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & DialogContentExtraProps
>(({ className, children, hideCloseButton, width = 'lg', tone, scrollable, ...props }, ref) => (
	<DialogPortal>
		<DialogOverlay />
		<DialogPrimitive.Content
			ref={ref}
			aria-describedby={props['aria-describedby'] || undefined}
			className={cn(
				'fixed left-[50%] top-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 rounded-[14px] border border-border bg-panel p-5 shadow-xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
				DIALOG_WIDTH[width],
				tone === 'card' && 'bg-card',
				scrollable && 'max-h-[90vh] overflow-y-auto',
				className,
			)}
			{...props}
		>
			{children}
			{!hideCloseButton && (
				<DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
					<X className="h-4 w-4" />
					<span className="sr-only">Close</span>
				</DialogPrimitive.Close>
			)}
		</DialogPrimitive.Content>
	</DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

/** Props di {@link DialogHeader}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Intestazione del dialog: contiene tipicamente {@link DialogTitle} e {@link DialogDescription}. */
const DialogHeader = ({ className, ...props }: DialogHeaderProps) => (
	<div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

/** Props di {@link DialogFooter}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Piè del dialog, tipicamente con i bottoni di azione. */
const DialogFooter = ({ className, ...props }: DialogFooterProps) => (
	<div
		className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
		{...props}
	/>
);
DialogFooter.displayName = 'DialogFooter';

export interface DialogTitleExtraProps {
	/** Dimensione del titolo: `sm` (compatta, usata dalla maggior parte dei dialog) o `base`. Default: `base`. */
	size?: 'sm' | 'base';
	/** Se true, mantiene il titolo solo per screen reader (nascosto visivamente), es. dialog di anteprima immagine. */
	visuallyHidden?: boolean;
	/** Se true, dispone icona + testo in linea. */
	withIcon?: boolean;
}

/** Titolo del dialog: obbligatorio per l'accessibilità anche quando nascosto visivamente (`visuallyHidden`). */
const DialogTitle = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & DialogTitleExtraProps
>(({ className, size = 'base', visuallyHidden, withIcon, ...props }, ref) => (
	<DialogPrimitive.Title
		ref={ref}
		className={cn(
			size === 'sm'
				? 'text-sm font-extrabold leading-none'
				: 'text-base font-extrabold leading-none',
			withIcon && 'flex items-center gap-2',
			visuallyHidden && 'sr-only',
			className,
		)}
		{...props}
	/>
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

/** Props di {@link DialogDescription}: nessuna prop custom, solo gli attributi HTML standard. */
export interface DialogDescriptionProps extends React.ComponentPropsWithoutRef<
	typeof DialogPrimitive.Description
> {}

/** Testo descrittivo del dialog, associato al titolo per l'accessibilità. */
const DialogDescription = React.forwardRef<
	React.ElementRef<typeof DialogPrimitive.Description>,
	DialogDescriptionProps
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		ref={ref}
		className={cn('text-sm text-muted-foreground', className)}
		{...props}
	/>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
	Dialog,
	DialogPortal,
	DialogOverlay,
	DialogClose,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogFooter,
	DialogTitle,
	DialogDescription,
};
